// ─────────────────────────────────────────────────────────────
//  Reporting-Mail-Core – EINZIGE QUELLE DER WAHRHEIT für die
//  Reporting-Erinnerungen (wer bekommt welche Mail, wann und mit
//  welchem Text).
//
//  Reines CommonJS ohne Abhängigkeiten, damit es BEIDE Welten nutzen
//  können:
//    • Frontend  -> src/Admin/StructureBoard.js / ReportingMailSettings.js
//                   importieren dieses Modul direkt.
//    • Cloud Fn  -> functions/index.js erhält eine 1:1-Kopie dieser
//                   Datei (functions/reportingMailCore.js), automatisch
//                   gespiegelt über den predeploy-Hook in firebase.json.
//
//  >>> NUR DIESE DATEI BEARBEITEN. Die Kopie in functions/ wird beim
//  >>> Deploy automatisch überschrieben – niemals dort von Hand ändern.
//
//  Ablauf der Erinnerungen (Outreach-Manager):
//    1. Am Fälligkeitstag des Reportings (availableAt) geht der erste
//       Reminder raus – danach jeden Tag erneut, bis eingereicht wurde.
//    2. Ist ein Reporting länger als `escalationDays` Tage überfällig,
//       bekommt zusätzlich die Admin-Adresse eine Sammel-Info.
//    3. Beim Einreichen geht eine Bestätigung an die Empfänger plus
//       Admin.
//
//  Der Versand selbst (SMTP) liegt in functions/mailer.js, die
//  Ausführung im Cron `sendReportingReminders`.
// ─────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

// Globale Einstellungen (Admin-Adresse, Eskalationsfrist) – Settings-Tab.
const REPORTING_MAIL_SETTINGS_PATH = 'salesgod-research/settings/reportingMail';
// Pro Kunde: wer bekommt die Reporting-Mails (Feld am Kundenobjekt).
const CUSTOMER_MAIL_FIELD = 'reportingMail';

const DEFAULT_ESCALATION_DAYS = 3;
// Sicherheitsnetz gegen Endlos-Erinnerungen bei vergessenen Altlasten.
// Danach bleibt das Reporting im Dashboard sichtbar, es geht aber keine
// weitere Mail mehr raus.
const DEFAULT_MAX_REMINDERS = 30;

// ── Kleine Helfer ─────────────────────────────────────────────

const cleanEmail = (v) => String(v == null ? '' : v).trim().toLowerCase();

// Absichtlich pragmatisch: verhindert Tippfehler, ohne RFC-Akrobatik.
const isEmail = (v) => /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i.test(cleanEmail(v));

// Freitext-Feld ("a@b.de, c@d.de" oder je Zeile) in eine Liste zerlegen.
const splitEmails = (v) =>
  String(v == null ? '' : v)
    .split(/[,;\s\n]+/)
    .map(cleanEmail)
    .filter(Boolean);

const uniqueEmails = (list) => {
  const seen = {};
  const out = [];
  (list || []).forEach((raw) => {
    const mail = cleanEmail(raw);
    if (!mail || seen[mail]) return;
    seen[mail] = true;
    out.push(mail);
  });
  return out;
};

// Rollen-Vergleich wie in der Pipeline: "Outreach-Manager", "outreach manager"
// und "outreach_manager" sind dasselbe.
const normalizeRole = (role) =>
  String(role == null ? '' : role).trim().toLowerCase().replace(/[\s_-]+/g, '-');
const isOutreachRole = (role) => normalizeRole(role).indexOf('outreach') !== -1;

// Tages-Schlüssel (UTC) als Idempotenz-Marke: pro Reporting geht höchstens
// eine Erinnerung pro Kalendertag raus. Der Cron läuft morgens in Wien –
// da ist der UTC-Tag identisch mit dem lokalen Tag.
const dayKey = (ms) => new Date(Number(ms) || 0).toISOString().slice(0, 10);

const fmtDate = (ms) => {
  const n = Number(ms);
  if (!isFinite(n) || n <= 0) return '–';
  return new Date(n).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

// periodKey "2026-03" -> "März 2026". Unbekannte Formate bleiben unverändert.
function periodLabel(periodKey) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(periodKey || '').trim());
  if (!m) return String(periodKey || '');
  const idx = parseInt(m[2], 10) - 1;
  if (idx < 0 || idx > 11) return String(periodKey || '');
  return `${MONTHS_DE[idx]} ${m[1]}`;
}

// Tage seit dem Fälligkeitstag, auf Kalendertage gerechnet: 0 = heute fällig,
// -1 = noch nicht fällig.
//
// Bewusst tagesbasiert und nicht stundengenau: ein Reporting schaltet um 09:00
// frei, der Cron läuft morgens. Mit einem reinen Zeitstempel-Vergleich wäre die
// „Erinnerung am Fälligkeitstag" sonst je nach Uhrzeit einen Tag zu spät.
const startOfDay = (ms) => {
  const d = new Date(Number(ms) || 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

function daysOverdue(reporting, nowMs = Date.now()) {
  const avail = Number(reporting && reporting.availableAt) || 0;
  if (!avail) return -1;
  const diff = Math.round((startOfDay(nowMs) - startOfDay(avail)) / DAY_MS);
  return diff < 0 ? -1 : diff;
}

// ── Einstellungen ─────────────────────────────────────────────

// Rohwerte aus der DB auf ein vollständiges Objekt mit Defaults bringen.
// Alle Aufrufer (Cron, Settings-UI) lesen ausschließlich hierüber.
function reportingMailSettings(raw) {
  const s = (raw && typeof raw === 'object') ? raw : {};
  const escalation = Math.round(Number(s.escalationDays));
  const maxReminders = Math.round(Number(s.maxReminders));
  return {
    // Reminder global scharf? Default an – sonst wäre das Feature nach dem
    // Deploy unsichtbar wirkungslos.
    enabled: s.enabled !== false,
    adminEmail: isEmail(s.adminEmail) ? cleanEmail(s.adminEmail) : '',
    escalationDays: isFinite(escalation) && escalation >= 0 ? escalation : DEFAULT_ESCALATION_DAYS,
    maxReminders: isFinite(maxReminders) && maxReminders > 0 ? maxReminders : DEFAULT_MAX_REMINDERS,
  };
}

// ── Empfänger pro Kunde ───────────────────────────────────────

// customer.reportingMail = { enabled, members: { <memberId>: true }, emails: [..], updatedAt }
//
// `configured` merkt sich, ob die Empfänger schon einmal bewusst gepflegt wurden.
// Nur solange das NICHT der Fall ist, greift der Outreach-Manager-Fallback – sonst
// würde ein absichtlich abgewählter Empfänger wieder auftauchen.
function normalizeMailConfig(raw) {
  const c = (raw && typeof raw === 'object') ? raw : {};
  const members = (c.members && typeof c.members === 'object') ? c.members : {};
  const emailsRaw = c.emails;
  let emails = [];
  if (Array.isArray(emailsRaw)) emails = emailsRaw;
  else if (emailsRaw && typeof emailsRaw === 'object') emails = Object.keys(emailsRaw).map((k) => emailsRaw[k]);
  else if (typeof emailsRaw === 'string') emails = splitEmails(emailsRaw);
  return {
    enabled: c.enabled !== false,
    configured: !!(raw && typeof raw === 'object' && (c.members || c.emails || c.updatedAt != null || c.enabled != null)),
    memberIds: Object.keys(members).filter((id) => members[id] != null && members[id] !== false),
    emails: uniqueEmails(emails).filter(isEmail),
  };
}

// Wer bekommt die Reporting-Mails dieses Kunden?
//
// Quelle ist ausschließlich die bewusste Auswahl am Kunden. Ist noch nichts
// ausgewählt, fallen wir auf die zugewiesenen Outreach-Manager mit E-Mail
// zurück – so funktionieren Bestandskunden ohne Nachpflegen sofort.
function resolveRecipients(customer, team, config) {
  const c = (customer && typeof customer === 'object') ? customer : {};
  const teamMap = (team && typeof team === 'object') ? team : {};
  const cfg = config || normalizeMailConfig(c[CUSTOMER_MAIL_FIELD]);

  const people = [];
  const pushMember = (id) => {
    const m = teamMap[id];
    if (!m || !isEmail(m.email)) return;
    people.push({ id, name: String(m.name || '').trim(), role: String(m.role || '').trim(), email: cleanEmail(m.email) });
  };

  cfg.memberIds.forEach(pushMember);

  let fallback = false;
  if (!cfg.configured && people.length === 0 && cfg.emails.length === 0) {
    const assigned = (c.team && typeof c.team === 'object') ? c.team : {};
    Object.keys(assigned)
      .filter((id) => assigned[id] != null)
      .filter((id) => teamMap[id] && isOutreachRole(teamMap[id].role))
      .forEach(pushMember);
    fallback = people.length > 0;
  }

  const to = uniqueEmails(people.map((p) => p.email).concat(cfg.emails));
  return {
    enabled: cfg.enabled,
    to,
    people,
    extraEmails: cfg.emails,
    // true = Empfänger stammen aus dem Outreach-Manager-Fallback, nicht aus
    // einer expliziten Auswahl. Die UI weist darauf hin.
    fallback,
  };
}

// Vorschlag für die Vorauswahl im Pipeline-Drawer: zugewiesene
// Outreach-Manager mit E-Mail-Adresse.
function suggestedMemberIds(customer, team) {
  const assigned = (customer && customer.team && typeof customer.team === 'object') ? customer.team : {};
  const teamMap = (team && typeof team === 'object') ? team : {};
  return Object.keys(assigned)
    .filter((id) => assigned[id] != null)
    .filter((id) => teamMap[id] && isOutreachRole(teamMap[id].role) && isEmail(teamMap[id].email));
}

// ── Entscheidung: geht heute eine Mail raus? ───────────────────

// Liefert für ein Reporting, was heute zu tun ist. Bewusst als reine
// Funktion, damit Cron und Dashboard exakt dieselbe Aussage treffen.
//
//   send      -> heute einen Reminder verschicken
//   escalate  -> zusätzlich in die Admin-Sammelmail aufnehmen
//   reason    -> warum NICHT gesendet wird (für Log & UI-Anzeige)
function reminderPlan(reporting, customer, settings, nowMs = Date.now()) {
  const s = reportingMailSettings(settings);
  const rep = (reporting && typeof reporting === 'object') ? reporting : null;
  const out = { send: false, escalate: false, reason: '', daysOverdue: -1, count: 0 };
  if (!rep) return Object.assign(out, { reason: 'kein-reporting' });

  out.count = Math.max(0, Math.round(Number(rep.reminderCount) || 0));
  out.daysOverdue = daysOverdue(rep, nowMs);

  if (!s.enabled) return Object.assign(out, { reason: 'global-deaktiviert' });
  if (rep.status === 'submitted' || typeof rep.submittedAt === 'number') {
    return Object.assign(out, { reason: 'eingereicht' });
  }
  const contract = (customer && customer.contract) || null;
  if (!contract || contract.active === false) {
    return Object.assign(out, { reason: 'vertrag-inaktiv' });
  }
  if (out.daysOverdue < 0) return Object.assign(out, { reason: 'noch-nicht-faellig' });
  if (out.count >= s.maxReminders) return Object.assign(out, { reason: 'max-erinnerungen' });
  if (rep.lastReminderDay === dayKey(nowMs)) {
    return Object.assign(out, { reason: 'heute-schon-gesendet' });
  }

  out.send = true;
  out.escalate = out.daysOverdue > s.escalationDays && rep.adminNotifiedDay !== dayKey(nowMs);
  return out;
}

// ── Mail-Texte ────────────────────────────────────────────────
//
// Jede Vorlage liefert { subject, text, html }. Der Text-Teil ist der
// verbindliche Inhalt, das HTML nur dessen hübschere Darstellung, damit die
// Mail auch in Text-Only-Clients vollständig lesbar bleibt.
//
// TON: sachlich und knapp, wie eine System-Erinnerung, die jemand sorgfältig
// formuliert hat. Der Status steht im ersten Satz, alles Weitere als Daten im
// Kasten, dazu genau eine Handlung. Kein Humor, keine Ausrufezeichen, keine
// Dringlichkeitsrhetorik: die Mail kommt täglich, sie muss ohne Effekte
// funktionieren. Du-Form, weil sie intern bleibt.
// Wie überall im BonusClub-Wording: KEINE Gedankenstriche, dafür Komma
// oder Punkt.

const escapeHtml = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// BonusClub-Palette: Beach Blue auf Weiß (siehe src/index.css, --blue und
// --skyblue). Der Header trägt das weiße Logo auf Beach Blue, der Inhalt steht
// auf einer weißen Karte.
// Content-ID des Logos: die Datei reist als Inline-Anhang mit der Mail (siehe
// functions/mailer.js). Eine normale Bild-URL wäre der einfachere Weg, aber
// Outlook und Hotmail laden externe Bilder bei unbekannten Absendern erst nach
// einer Freigabe, und dann steht im Header nur ein leerer Platzhalter.
const LOGO_CID = 'bonusclub-logo';

const BRAND = {
  name: 'SalesGod by BonusClub',
  site: 'https://www.bonusclub.io',
  logo: `cid:${LOGO_CID}`,
  logoWidth: 176,
  logoHeight: 34,
  beach: '#6897B7',
  deep: '#3f6e8f',
  sky: '#D9E4F8',
  tint: '#f2f7fc',
  line: '#dce7f3',
  ink: '#1b2b36',
  muted: '#6d8496',
};

const SIGNATURE = 'Julian';
const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

// Anrede: mit Vornamen, wenn genau eine Person adressiert ist, sonst neutral.
function greetingFor(name, recipientCount) {
  const first = String(name || '').trim().split(/\s+/)[0];
  if (first) return `Hi ${first},`;
  return (Number(recipientCount) || 0) > 1 ? 'Hi zusammen,' : 'Hi,';
}

// Gemeinsames Grundgerüst aller Mails: Logo-Header, weiße Karte, Fußnote.
//
// Bewusst Tabellen statt Flex/Grid und alle Styles inline: Outlook rendert mit
// Word als Engine und ignoriert modernes CSS. Der Header hat zusätzlich eine
// Hintergrundfarbe, damit er auch dann sauber aussieht, wenn der Client das
// Logo blockt (Standard bei vielen Postfächern beim ersten Öffnen).
function htmlShell(title, blocks, footer) {
  const body = (blocks || []).filter(Boolean).join('\n');
  return [
    `<div style="margin:0;padding:28px 12px;background:${BRAND.sky};font-family:${FONT};">`,
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">',
    '<tr><td align="center">',
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="580" style="width:100%;max-width:580px;border-collapse:separate;background:#ffffff;border-radius:18px;overflow:hidden;border:1px solid ${BRAND.line};">`,

    // Header: weißes Logo auf Beach Blue. Der Alt-Text ist weiß und fett
    // formatiert, damit die Kopfzeile auch ohne Bild lesbar bleibt.
    `<tr><td bgcolor="${BRAND.beach}" style="background:${BRAND.beach};padding:22px 28px;">`,
    `<a href="${BRAND.site}" style="text-decoration:none;">`,
    `<img src="${BRAND.logo}" width="${BRAND.logoWidth}" height="${BRAND.logoHeight}" alt="${escapeHtml(BRAND.name)}"`,
    ` style="display:block;border:0;outline:none;text-decoration:none;width:${BRAND.logoWidth}px;height:auto;max-width:100%;`,
    `color:#ffffff;font-family:${FONT};font-size:17px;font-weight:700;line-height:34px;">`,
    '</a></td></tr>',

    // Inhalt.
    `<tr><td style="padding:30px 28px 10px;">`,
    `<h1 style="margin:0 0 18px;font-family:${FONT};font-size:20px;line-height:1.35;font-weight:700;color:${BRAND.ink};">${escapeHtml(title)}</h1>`,
    body,
    '</td></tr>',

    // Fußnote in der Karte.
    footer
      ? `<tr><td style="padding:4px 28px 24px;"><div style="border-top:1px solid ${BRAND.line};padding-top:14px;color:${BRAND.muted};font-family:${FONT};font-size:12px;line-height:1.6;">${escapeHtml(footer)}</div></td></tr>`
      : '<tr><td style="height:16px;"></td></tr>',

    '</table>',

    // Absender-Zeile unter der Karte.
    `<div style="max-width:580px;margin:14px auto 0;color:${BRAND.muted};font-family:${FONT};font-size:11px;line-height:1.6;text-align:center;">`,
    `BonusClub &middot; <a href="${BRAND.site}" style="color:${BRAND.deep};text-decoration:none;">bonusclub.io</a>`,
    '</div>',

    '</td></tr></table></div>',
  ].join('\n');
}

const p = (text) => `<p style="margin:0 0 13px;font-family:${FONT};font-size:15px;line-height:1.65;color:${BRAND.ink};">${escapeHtml(text)}</p>`;
const signOff = (line) => [
  line ? p(line) : '',
  `<p style="margin:20px 0 8px;font-family:${FONT};font-size:15px;line-height:1.6;font-weight:600;color:${BRAND.ink};">${escapeHtml(SIGNATURE)}</p>`,
].filter(Boolean).join('\n');

// Kennzahlen-Kasten (Kunde, Zeitraum, Frist).
function factBox(rows) {
  const cells = (rows || []).filter(Boolean).map(([label, value]) => [
    '<tr>',
    `<td style="padding:6px 14px 6px 18px;font-family:${FONT};color:${BRAND.muted};font-size:13px;line-height:1.5;white-space:nowrap;">${escapeHtml(label)}</td>`,
    `<td style="padding:6px 18px 6px 0;font-family:${FONT};color:${BRAND.ink};font-size:13px;line-height:1.5;font-weight:600;">${escapeHtml(value)}</td>`,
    '</tr>',
  ].join('')).join('');
  if (!cells) return '';
  return [
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0 0 20px;border-collapse:separate;background:${BRAND.tint};`,
    `border:1px solid ${BRAND.line};border-radius:12px;">`,
    '<tr><td colspan="2" style="height:10px;"></td></tr>',
    cells,
    '<tr><td colspan="2" style="height:10px;"></td></tr>',
    '</table>',
  ].join('');
}

// Button als Tabelle, damit Outlook die Hintergrundfarbe zuverlaessig faerbt.
function button(label, href) {
  if (!href) return '';
  return [
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 20px;border-collapse:separate;">',
    `<tr><td bgcolor="${BRAND.deep}" style="background:${BRAND.deep};border-radius:10px;">`,
    `<a href="${escapeHtml(href)}" style="display:inline-block;padding:14px 24px;font-family:${FONT};`,
    'font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;border-radius:10px;">',
    `${escapeHtml(label)}</a></td></tr></table>`,
  ].join('');
}

// Wortlaut der Erinnerung: drei Zustände, ein fester Satzbau. Bewusst ohne
// Textvarianten, damit die tägliche Mail immer gleich gelesen werden kann und
// nur der Status wechselt.
function reminderCopy(company, days, deadlinePassed) {
  const dayWord = `${days} ${days === 1 ? 'Tag' : 'Tagen'}`;

  if (deadlinePassed) {
    return {
      stage: 'late',
      subject: `Überfällig: Reporting ${company}`,
      headline: `Reporting ${company} ist überfällig`,
      opener: `das Reporting für ${company} ist seit ${dayWord} fällig und noch nicht eingereicht. Die Abgabefrist ist abgelaufen.`,
      note: 'Solange die Zahlen fehlen, können Abrechnung und Kundenkommunikation für diesen Zeitraum nicht abgeschlossen werden.',
      closer: 'Bei Rückfragen antworte einfach auf diese Mail.',
    };
  }
  if (days === 0) {
    return {
      stage: 'today',
      subject: `Heute fällig: Reporting ${company}`,
      headline: `Reporting ${company} ist heute fällig`,
      opener: `das Reporting für ${company} ist ab heute fällig. Das Formular ist freigeschaltet.`,
      note: '',
      closer: 'Danke dir.',
    };
  }
  return {
    stage: 'open',
    subject: `Seit ${dayWord} offen: Reporting ${company}`,
    headline: `Reporting ${company} ist seit ${dayWord} offen`,
    opener: `das Reporting für ${company} ist seit ${dayWord} fällig und noch nicht eingereicht.`,
    note: '',
    closer: 'Danke dir.',
  };
}

// Erinnerung an ein fälliges oder überfälliges Reporting.
function buildReminderMail(info) {
  const i = info || {};
  const company = String(i.companyName || i.customerSlug || 'Kunde');
  const period = periodLabel(i.periodKey);
  const over = Math.max(0, Math.round(Number(i.daysOverdue) || 0));
  const now = Number(i.now || Date.now());
  const deadlinePassed = typeof i.deadlineAt === 'number' && now > i.deadlineAt;
  const copy = reminderCopy(company, over, deadlinePassed);
  const greeting = greetingFor(i.greetingName, i.recipientCount);

  const periodRange = `${fmtDate(i.periodStart)} bis ${fmtDate(i.periodEnd)}`;
  const rows = [
    ['Kunde', company],
    ['Zeitraum', periodRange],
    over === 0 ? ['Fällig', 'heute'] : ['Fällig seit', fmtDate(i.availableAt)],
    ['Abgabefrist', `${fmtDate(i.deadlineAt)}${deadlinePassed ? ' (abgelaufen)' : ''}`],
    // Der Zugang steht absichtlich in der Mail: Empfänger sind ausschliesslich
    // interne, am Kunden hinterlegte Adressen, und ohne Passwort waere der Link
    // eine Sackgasse.
    i.password ? ['Passwort', String(i.password)] : null,
  ];

  const text = [
    greeting,
    '',
    copy.opener,
    copy.note ? '' : null,
    copy.note || null,
    '',
    ...rows.filter(Boolean).map(([label, value]) => `${label}: ${value}`),
    i.link ? '' : null,
    i.link ? `Reporting öffnen: ${i.link}` : null,
    '',
    copy.closer,
    '',
    SIGNATURE,
  ].filter((l) => l !== null).join('\n');

  const html = htmlShell(copy.headline, [
    p(greeting),
    p(copy.opener),
    copy.note ? p(copy.note) : '',
    factBox(rows),
    button('Reporting öffnen', i.link),
    signOff(copy.closer),
  ], 'Automatische Erinnerung aus dem BonusClub Dashboard. Sie wiederholt sich täglich, bis das Reporting eingereicht ist.');

  return { subject: `${copy.subject} (${period})`, text, html };
}

// Bestätigung nach dem Einreichen.
function buildConfirmationMail(info) {
  const i = info || {};
  const company = String(i.companyName || i.customerSlug || 'Kunde');
  const period = periodLabel(i.periodKey);
  const qualified = Math.max(0, Math.round(Number(i.qualifiedAppointments) || 0));
  const extra = Math.max(0, Math.round(Number(i.extraAppointments) || 0));
  const greeting = greetingFor(i.greetingName, i.recipientCount);
  const periodRange = `${fmtDate(i.periodStart)} bis ${fmtDate(i.periodEnd)}`;
  const late = typeof i.deadlineAt === 'number' && typeof i.submittedAt === 'number' && i.submittedAt > i.deadlineAt;
  const lateNote = late ? `Die Abgabe erfolgte nach der Frist vom ${fmtDate(i.deadlineAt)}.` : '';

  const opener = `das Reporting für ${company} ist eingereicht. Der Zeitraum ${period} ist damit abgeschlossen, weitere Erinnerungen entfallen.`;

  const rows = [
    ['Kunde', company],
    ['Zeitraum', periodRange],
    ['Eingereicht am', fmtDate(i.submittedAt)],
    i.submittedBy ? ['Eingereicht von', String(i.submittedBy)] : null,
    ['Qualifizierte Termine', String(qualified)],
    extra > 0 ? ['davon über dem Kontingent', String(extra)] : null,
  ];

  const text = [
    greeting,
    '',
    opener,
    lateNote ? '' : null,
    lateNote || null,
    '',
    ...rows.filter(Boolean).map(([label, value]) => `${label}: ${value}`),
    i.link ? '' : null,
    i.link ? `Reporting ansehen: ${i.link}` : null,
    '',
    'Danke dir.',
    '',
    SIGNATURE,
  ].filter((l) => l !== null).join('\n');

  const html = htmlShell(`Reporting ${company} ist eingereicht`, [
    p(greeting),
    p(opener),
    lateNote ? p(lateNote) : '',
    factBox(rows),
    button('Reporting ansehen', i.link),
    signOff('Danke dir.'),
  ], 'Für diesen Zeitraum gehen keine weiteren Erinnerungen raus.');

  return { subject: `Eingereicht: Reporting ${company} (${period})`, text, html };
}

// Sammel-Info an die Admin-Adresse: alles, was deutlich überfällig ist.
// Bewusst nüchtern, das ist die Kontroll-Übersicht und keine Reminder-Mail.
function buildAdminEscalationMail(info) {
  const i = info || {};
  const items = Array.isArray(i.items) ? i.items : [];
  const days = Math.max(0, Math.round(Number(i.escalationDays) || DEFAULT_ESCALATION_DAYS));
  const subject = items.length === 1
    ? `Überfällig: Reporting ${items[0].companyName} (${items[0].daysOverdue} Tage)`
    : `${items.length} überfällige Reportings (über ${days} Tage)`;

  const who = (it) => ((it.recipients && it.recipients.length) ? it.recipients.join(', ') : 'kein Empfänger hinterlegt');
  const lines = items.map((it) =>
    `• ${it.companyName} (${periodLabel(it.periodKey)}), ${it.daysOverdue} Tage offen, ${it.reminderCount}x erinnert, ${who(it)}`);

  const rowsHtml = items.map((it) => [
    '<tr>',
    `<td style="padding:10px 12px 10px 0;border-top:1px solid ${BRAND.line};font-size:14px;font-weight:600;color:${BRAND.ink};">`,
    `${escapeHtml(it.companyName)}<span style="display:block;font-weight:400;color:${BRAND.muted};font-size:12px;">${escapeHtml(periodLabel(it.periodKey))}, ${escapeHtml(who(it))}</span></td>`,
    `<td style="padding:10px 0;border-top:1px solid ${BRAND.line};font-size:13px;text-align:right;white-space:nowrap;color:${BRAND.ink};">`,
    `${escapeHtml(String(it.daysOverdue))} Tage<span style="display:block;color:${BRAND.muted};font-size:12px;">${escapeHtml(String(it.reminderCount))}x erinnert</span></td>`,
    '</tr>',
  ].join('')).join('');

  const intro = `Diese Reportings sind seit mehr als ${days} Tagen offen. Die täglichen Erinnerungen an die hinterlegten Empfänger laufen weiter.`;

  const text = [
    `Diese Reportings sind seit mehr als ${days} Tagen offen:`,
    '',
    lines.join('\n'),
    '',
    i.link ? `Reporting-Übersicht: ${i.link}` : null,
  ].filter((l) => l !== null).join('\n');

  const html = htmlShell(`Überfällige Reportings (mehr als ${days} Tage)`, [
    p(intro),
    `<table style="width:100%;border-collapse:collapse;margin:0 0 18px;"><tbody>${rowsHtml}</tbody></table>`,
    button('Reporting-Übersicht öffnen', i.link),
  ], 'Diese Übersicht kommt täglich, solange etwas offen ist.');

  return { subject, text, html };
}

// Test-Mail aus den Einstellungen: prüft nur, ob der Versandweg funktioniert.
function buildTestMail() {
  const subject = 'Testmail: Reporting-Erinnerungen';
  const line1 = 'diese Mail bestätigt, dass der Versandweg für die Reporting-Erinnerungen funktioniert.';
  const line2 = 'Postfach, Absender und Empfängeradresse sind damit geprüft. Es ist nichts zu tun.';
  const text = ['Hi,', '', line1, '', line2].join('\n');
  const html = htmlShell('Der Versandweg funktioniert', [
    p('Hi,'),
    p(line1),
    p(line2),
  ], 'Manuell ausgelöst über Settings, Reporting-Mails im Team Dashboard.');
  return { subject, text, html };
}

module.exports = {
  DAY_MS,
  REPORTING_MAIL_SETTINGS_PATH,
  CUSTOMER_MAIL_FIELD,
  DEFAULT_ESCALATION_DAYS,
  DEFAULT_MAX_REMINDERS,
  cleanEmail,
  isEmail,
  splitEmails,
  uniqueEmails,
  isOutreachRole,
  dayKey,
  fmtDate,
  periodLabel,
  daysOverdue,
  reportingMailSettings,
  normalizeMailConfig,
  resolveRecipients,
  suggestedMemberIds,
  reminderPlan,
  buildReminderMail,
  buildConfirmationMail,
  buildAdminEscalationMail,
  buildTestMail,
  LOGO_CID,
};
